/**
 * Capa 3: Cliente API — Onboarding de Clientes
 * Conecta los endpoints FastAPI de /api/v1/onboarding/clientes
 */

import { apiClient } from '@/config/api';
import {
  PasoWizard,
  CanalRegistro,
  InicializarOnboardingResponse,
  ValidarIdentificadorResponse,
  SesionOnboardingResponse,
  CompletarOnboardingPayload,
  CompletarOnboardingResponse,
  AbandonoOnboardingPayload,
  AbandonoOnboardingResponse,
  AutocompletarVozPayload,
  AutocompletarVozResponse,
  AutocompletarDocumentoPayload,
  AutocompletarDocumentoResponse,
  MetricasFunnelResponse,
  EventosOnboardingPaginadosResponse,
} from '../types/onboarding.types';

const BASE_URL = '/onboarding/clientes';

export const onboardingApi = {
  /**
   * Abre la sesión efímera en Redis (TTL 7200s) e inicia la telemetría del embudo.
   */
  async inicializar(
    canal_registro: CanalRegistro = 'WEB_SPA',
    origen_lead: string = 'ORGANICO'
  ): Promise<InicializarOnboardingResponse> {
    const response = await apiClient.post<InicializarOnboardingResponse>(`${BASE_URL}/inicializar`, {
      canal_registro,
      origen_lead,
    });
    return response.data;
  },

  /**
   * Actualiza incrementalmente los datos de un paso en la memoria volátil de Redis.
   */
  async actualizarPaso(
    id_onboarding: string,
    paso: PasoWizard,
    datos_paso: Record<string, unknown>
  ): Promise<void> {
    await apiClient.put(`${BASE_URL}/paso`, {
      id_onboarding,
      paso,
      datos_paso,
    });
  },

  /**
   * Obtiene el estado acumulado de la sesión desde Redis para reanudación tras refresh.
   */
  async obtenerSesion(id_onboarding: string): Promise<SesionOnboardingResponse> {
    const response = await apiClient.get<SesionOnboardingResponse>(
      `${BASE_URL}/sesion/${id_onboarding}`
    );
    return response.data;
  },

  /**
   * Centinela anti-colisión en debounce para verificar disponibilidad de email o teléfono.
   */
  async validarIdentificador(
    tipo: 'EMAIL' | 'TELEFONO',
    valor: string
  ): Promise<ValidarIdentificadorResponse> {
    const response = await apiClient.post<ValidarIdentificadorResponse>(
      `${BASE_URL}/validar-identificador`,
      { tipo, valor }
    );
    return response.data;
  },

  /**
   * Ejecuta la transacción ACID en backend: Granja + Usuario Propietario + Sesión y retorna JWT (Zero-Friction Handover).
   */
  async completar(payload: CompletarOnboardingPayload): Promise<CompletarOnboardingResponse> {
    const response = await apiClient.post<CompletarOnboardingResponse>(
      `${BASE_URL}/completar`,
      payload
    );
    return response.data;
  },

  /**
   * Registra explícitamente el evento analítico de abandono cuando el usuario deserta o cancela.
   */
  async abandonar(payload: AbandonoOnboardingPayload): Promise<AbandonoOnboardingResponse> {
    const response = await apiClient.post<AbandonoOnboardingResponse>(
      `${BASE_URL}/abandonar`,
      payload
    );
    return response.data;
  },

  /**
   * FN-ONB-CLI-09: Recupera la sesión de onboarding mediante el token del Magic Link.
   */
  async recuperarDesdeMagicLink(token: string): Promise<SesionOnboardingResponse> {
    const response = await apiClient.get<SesionOnboardingResponse>(
      `${BASE_URL}/recuperar/${encodeURIComponent(token)}`
    );
    return response.data;
  },

  /**
   * FN-ONB-CLI-05A: Autocompleta los pasos 1 y 2 del wizard dictando una nota de voz.
   */
  async autocompletarPorVoz(payload: AutocompletarVozPayload): Promise<AutocompletarVozResponse> {
    const response = await apiClient.post<AutocompletarVozResponse>(
      `${BASE_URL}/autocompletar-voz`,
      payload
    );
    return response.data;
  },

  /**
   * FN-ONB-CLI-05B: Autocompleta los pasos 1 y 2 del wizard escaneando el RUT DIAN o Certificado ICA.
   */
  async autocompletarPorDocumento(
    payload: AutocompletarDocumentoPayload
  ): Promise<AutocompletarDocumentoResponse> {
    const response = await apiClient.post<AutocompletarDocumentoResponse>(
      `${BASE_URL}/autocompletar-documento`,
      payload
    );
    return response.data;
  },

  /**
   * FN-ONB-CLI-08: Obtiene métricas consolidadas del embudo de onboarding (Consola Superadmin).
   */
  async obtenerMetricasFunnel(params?: {
    fecha_desde?: string;
    fecha_hasta?: string;
    canal?: CanalRegistro;
  }): Promise<MetricasFunnelResponse> {
    const response = await apiClient.get<MetricasFunnelResponse>(`${BASE_URL}/metricas/funnel`, {
      params,
    });
    return response.data;
  },

  /**
   * Obtiene listado paginado de eventos del embudo de onboarding (Consola Superadmin).
   */
  async listarEventosOnboarding(params?: {
    estado_registro?: string;
    canal_registro?: string;
    limit?: number;
    offset?: number;
  }): Promise<EventosOnboardingPaginadosResponse> {
    const response = await apiClient.get<EventosOnboardingPaginadosResponse>(
      `${BASE_URL}/eventos`,
      { params }
    );
    return response.data;
  },
};
